import { Container, Row, Col, Card, CardBody, Badge, Button, Spinner, Image } from 'react-bootstrap';
import Link from 'next/link';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { BsArrowRight } from 'react-icons/bs';
import { destinationsService } from '@/api/services';
import { useEffect, useState } from 'react';
import { getDestinationImage } from '@/utils/imageUrl';
import { isRuntimeDev } from '@/utils/runtimeEnv';
// Import map image
import eastCreteMap from '@/assets/images/maps/east-crete-map.png';

// Fallback images if destination doesn't have an image
import tour1 from '@/assets/images/category/tour/01.jpg';
import tour2 from '@/assets/images/category/tour/02.jpg';
import tour3 from '@/assets/images/category/tour/03.jpg';
import tour4 from '@/assets/images/category/tour/04.jpg';
import tour5 from '@/assets/images/category/tour/05.jpg';
import tour6 from '@/assets/images/category/tour/06.jpg';

export const eastCreteFallbackImages = [tour1, tour2, tour3, tour4, tour5, tour6];

// Color options for destinations (cycling through)
const colors = ['primary', 'success', 'info', 'warning', 'danger'];

export const useEastCreteDestinations = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await destinationsService.getAll();

        // Handle response - data might be in response.data.data or response.data
        const destinationsData = response.data?.data || response.data || [];

        if (!Array.isArray(destinationsData) || destinationsData.length === 0) {
          setDestinations([]);
          return;
        }

        // Sort by order field and limit to 6 for homepage
        const sortedDestinations = destinationsData
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .slice(0, 6);
        setDestinations(sortedDestinations);
      } catch (err) {
        // Handle 401 Unauthorized - endpoint requires authentication
        if (err.response?.status === 401) {
          if (isRuntimeDev()) {
          // Backend endpoint requires authentication - log for debugging
            console.warn('Destinations API 401 Unauthorized:', {
            message: 'The backend /destinations endpoint requires authentication.',
            solution: 'Configure the backend to allow public access to GET /destinations endpoint (similar to /accommodations and /excursions)',
            endpoint: err.config?.url || '/destinations',
            fix: 'Add GET /destinations to your public routes in the backend (check routes configuration for public endpoints)'
          });
          }

          // Use fallback data so section still displays
          // This allows the page to work while backend is being configured
          const fallbackDestinations = [
            { id: 1, name: 'Gouves', description: 'Family-friendly resort area with beautiful beaches and traditional villages.', order: 1 },
            { id: 2, name: 'Hersonissos', description: 'Vibrant coastal town known for its nightlife and sandy beaches.', order: 2 },
            { id: 3, name: 'Elounda', description: 'Luxury resort destination with crystal-clear waters and upscale hotels.', order: 3 },
            { id: 4, name: 'Agios Nikolaos', description: 'Picturesque town on the Gulf of Mirabello with charming waterfront.', order: 4 },
            { id: 5, name: 'Ierapetra', description: 'Southern coastal town with the longest beach in Crete.', order: 5 },
            { id: 6, name: 'Sitia', description: 'Easternmost city with authentic local culture and stunning landscapes.', order: 6 }
          ];
          setDestinations(fallbackDestinations);
          setError('USING_FALLBACK_DATA'); // Special flag for fallback mode
        } else if (err.code === 'ERR_NETWORK' || err.response?.status === 404) {
          if (isRuntimeDev()) {
            console.warn('Destinations endpoint not available. Please ensure the backend is running and the endpoint is implemented.');
          }
          setError('Destinations endpoint not available. Please ensure the backend is running and the endpoint is implemented.');
          setDestinations([]);
        } else {
          if (isRuntimeDev()) {
            console.warn(err.response?.data?.message || err.message || 'Failed to load destinations. Please try again later.');
          }
          setError(err.response?.data?.message || err.message || 'Failed to load destinations. Please try again later.');
          setDestinations([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
  }, []);

  return { destinations, loading, error };
};

export const getEastCreteDestinationImage = (destination, index) => {
  return getDestinationImage(destination, eastCreteFallbackImages[index % eastCreteFallbackImages.length]);
};

const DiscoverSouthCrete = ({ showDestinationGrid = true }) => {
  const { destinations, loading, error } = useEastCreteDestinations();

  if (loading) {
    return (
      <section className="py-5 py-lg-6 bg-light">
        <Container>
          <Row className="mb-4">
            <Col lg={10} className="mx-auto text-center">
              <Badge bg="primary" className="mb-3 px-3 py-2">
                <FaMapMarkerAlt className="me-2" />
                Discover Our Region
              </Badge>
              <h2 className="sec-title mb-3">
                Explore Beautiful East Crete
              </h2>
              <div className="d-flex justify-content-center">
                <Spinner animation="border" variant="primary" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  // If using fallback data (401 error), still show the section
  // Otherwise, if other error or no destinations, hide the section
  if (error && error !== 'USING_FALLBACK_DATA') {
    if (error !== '401_AUTH_REQUIRED' && destinations.length === 0) {
      return null;
    }
  }

  // If no destinations (and not using fallback), hide section
  if (!error && destinations.length === 0) {
    return null;
  }
  return (
    <section className="py-5 py-lg-6 bg-light">
      <Container>
        <Row className="mb-3">
          <Col lg={10} className="mx-auto text-center">
            <Badge bg="primary" className="mb-3 px-3 py-2">
              <FaMapMarkerAlt className="me-2" />
              Discover Our Region
            </Badge>
            <h2 className="sec-title mb-3">
              Explore Beautiful East Crete
            </h2>
            <p className="lead mb-4">
              East Crete extends from Heraklion to Palekastro in Sitia, offering diverse experiences 
              from vibrant beach towns to traditional villages, luxury resorts to authentic local experiences. 
              Discover the region where all our services are located.
            </p>
            <p className="text-muted mb-0">
              From Gouves in the west to Palekastro in the east, East Crete consists of several 
              destinations that have attracted travelers for decades, each with its unique character and charm.
            </p>
          </Col>
        </Row>

        {/* East Crete Map - Same width as other sections */}
        <Row className="mb-4">
          <Col>
            <div 
              className="position-relative w-100 bg-light"
              style={{
                padding: '2rem 0',
                borderRadius: '1rem'
              }}
            >
              <img 
                src={eastCreteMap} 
                alt="East Crete Map" 
                className="w-100 h-auto"
                style={{
                  display: 'block',
                  objectFit: 'contain',
                  opacity: 0.9
                }}
              />
              {/* Subtle gradient fade overlay for smooth edges */}
              <div 
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{
                  background: 'linear-gradient(to bottom, rgba(245, 245, 246, 0.2) 0%, transparent 15%, transparent 85%, rgba(245, 245, 246, 0.2) 100%)',
                  pointerEvents: 'none',
                  borderRadius: '1rem'
                }}
              />
            </div>
          </Col>
        </Row>

        {showDestinationGrid && (
          <Row className="g-4 mb-4">
            {destinations.map((destination, idx) => {
              const imageUrl = getEastCreteDestinationImage(destination, idx);
            const color = colors[idx % colors.length];
            
            return (
              <Col key={destination.id} sm={6} lg={4}>
                <Card className="border-0 shadow-sm h-100 card-hover-shadow overflow-hidden">
                  <div className="position-relative" style={{ height: '200px', overflow: 'hidden' }}>
                    <Image 
                      src={imageUrl} 
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                      alt={destination.name || `Destination ${idx + 1}`}
                      onError={(e) => {
                        // Fallback to default image on error
                        e.target.onerror = null;
                        e.target.src = eastCreteFallbackImages[idx % eastCreteFallbackImages.length];
                      }}
                    />
                  </div>
                  <CardBody className="p-4">
                    <div className={`icon-lg bg-${color} bg-opacity-10 text-${color} rounded-circle flex-centered mb-3`}>
                      <FaMapMarkerAlt size={24} />
                    </div>
                    <h5 className="mb-2 fw-bold">{destination.name}</h5>
                    {destination.description && (
                      <p className="text-muted small mb-3" style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }} dangerouslySetInnerHTML={{ __html: destination.description }} />
                    )}
                  </CardBody>
                </Card>
              </Col>
            );
            })}
          </Row>
        )}

        <Row>
          <Col>
            <div className="bg-white rounded-4 p-4 p-lg-5 shadow-sm border-start border-4 border-primary text-center">
              <h4 className="mb-3 fw-bold">Discover All East Crete Destinations</h4>
              <p className="text-muted mb-4">
                From Gouves and Hersonissos to Ierapetra and Sitia, explore detailed information about 
                each destination, including beaches, attractions, accommodations, and local experiences.
              </p>
              <Button as={Link} href="/east-crete" variant="primary" size="lg">
                Explore East Crete Guide
                <BsArrowRight className="ms-2" />
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default DiscoverSouthCrete;

